import React, { useState, useEffect } from "react"
import {
  Box,
  AccordionDetails,
  Button
} from "@mui/material"
import { 
  ArrowForward, 
  ExpandMore, 
  Edit, 
  CalendarMonth, 
  Work, 
  AccessTime, 
  LocationCity, 
  EmojiEvents,
  WorkHistory,
  ViewWeek
} from "@mui/icons-material"
import { useHistory } from 'react-router'
import { EditPostMode, EditedJob, SevenBinaryArray } from "../types"
import { TopBarRedesigned } from "components/TopBarRedesigned"
import { exitReact, formatFullLocation } from "utils"
import dayjs from "dayjs"
import { 
  MainContainer, 
  ContentContainer, 
  PageTitle, 
  LocationPaper, 
  LocationTitle, 
  LocationText, 
  StyledAccordion, 
  StyledAccordionSummary, 
  AccordionTitleContainer, 
  AccordionTitle, 
  DetailsGrid, 
  DetailItemContainer, 
  IconContainer, 
  DetailLabelText, 
  DetailValueText, 
  DetailContentText
} from "./OverviewStep.styles"
import { Days } from "../config"

export interface OverviewStepProps {
  job: EditedJob
  onEdit: () => void
  onClose: () => void
  onConfirm: () => void
}

const OverviewStep: React.FC<OverviewStepProps> = ({
  job,
  onEdit,
  onClose,
  onConfirm
}) => {
  const history = useHistory();
  const [expandedPanels, setExpandedPanels] = useState<{[key: string]: boolean}>({
    jobDetails: true,
    jobTitle: true,
    jobDescription: true
  });
  
  const handleAccordionChange = (panel: string) => (event: React.SyntheticEvent, isExpanded: boolean) => {
    setExpandedPanels({ ...expandedPanels, [panel]: isExpanded });
  };
  
  const renderAvailabilityDays = () => {
    // Handle case where availability is undefined or empty
    if (!job.availability || !Array.isArray(job.availability) || job.availability.length < 1) {
      return 'Not specified';
    }
    
    try {
      if (typeof job.availability[0] === 'object' && job.availability[0] !== null) {
        return job.availability
          .filter(day => day && typeof day === 'object' && 'text' in day)
          .map(day => (day as any).text)
          .join(', ') || 'Not specified';
      }
      
      // Handle case where availability is a binary array [0,1,0,1,0,0,0]
      if (typeof job.availability[0] === 'number') {
        const selectedDays = job.availability
          .map((value, index) => value === 1 ? Days[index].text : null)
          .filter(day => day !== null);
        
        return selectedDays.length > 0 ? selectedDays.join(', ') : 'Not specified';
      }
      
      return 'Not specified';
    } catch (error) {
      console.error("Error rendering availability days:", error);
      return 'Not specified';
    }
  };

  return (
    <>
      <TopBarRedesigned
        onBack={() => {
          history.push('loading')
          exitReact('home')
        }}
      />
      <MainContainer>
        <ContentContainer>
          <PageTitle variant="h4">
            Post a Permanent / Contract Pos
          </PageTitle>

          <LocationPaper elevation={1}>
            <LocationTitle variant="h6">
              Job Posting for Location
            </LocationTitle>
            <LocationText variant="body1">
              {job.location?.practiceName}
            </LocationText>
            <LocationText variant="body1">
              {job.location ? formatFullLocation(job.location) : ""}
            </LocationText>
          </LocationPaper>
          
          <StyledAccordion 
            expanded={expandedPanels.jobDetails}
            onChange={handleAccordionChange('jobDetails')}
          >
            <StyledAccordionSummary expandIcon={<ExpandMore />}>
              <AccordionTitleContainer>
                <AccordionTitle variant="h6">Job Details</AccordionTitle>
              </AccordionTitleContainer>
            </StyledAccordionSummary>
            <AccordionDetails>
              <DetailsGrid>
                <DetailItemContainer>
                  <IconContainer>
                    <Work sx={{
                      color: 'var(--primary-blue)',
                      fontSize: "1.5rem",
                      padding: "4px",
                      bgcolor: "#e3f2fd",
                      borderRadius: '8px',
                    }} />
                  </IconContainer>
                  <Box>
                    <DetailLabelText variant="body2">Role</DetailLabelText>
                    <DetailValueText>{job.position?.text || "Not specified"}</DetailValueText>
                  </Box>
                </DetailItemContainer>
                
                <DetailItemContainer>
                  <IconContainer>
                    <AccessTime sx={{
                      color: 'var(--primary-blue)',
                      fontSize: "1.5rem",
                      padding: "4px",
                      bgcolor: "#e3f2fd",
                      borderRadius: '8px',
                    }} />
                  </IconContainer>
                  <Box>
                    <DetailLabelText variant="body2">Contract</DetailLabelText>
                    <DetailValueText>{job.basis?.text || "Not specified"}</DetailValueText>
                  </Box>
                </DetailItemContainer>
                
                <DetailItemContainer>
                  <IconContainer>
                    <CalendarMonth sx={{
                      color: 'var(--primary-blue)',
                      fontSize: "1.5rem",
                      padding: "4px",
                      bgcolor: "#e3f2fd",
                      borderRadius: '8px',
                    }} />
                  </IconContainer>
                  <Box>
                    <DetailLabelText variant="body2">Start Date</DetailLabelText>
                    <DetailValueText>
                      {job.startDate ? dayjs(job.startDate).format('MMMM D, YYYY') : "Not specified"}
                      {job.approxStart ? " (approximate)" : ""}
                    </DetailValueText>
                  </Box>
                </DetailItemContainer>
                
                <DetailItemContainer>
                  <IconContainer>
                    <ViewWeek sx={{
                      color: 'var(--primary-blue)',
                      fontSize: "1.5rem",
                      padding: "4px",
                      bgcolor: "#e3f2fd",
                      borderRadius: '8px',
                    }} />
                  </IconContainer>
                  <Box>
                    <DetailLabelText variant="body2">Days Needed</DetailLabelText>
                    <DetailValueText>{renderAvailabilityDays()}</DetailValueText>
                  </Box>
                </DetailItemContainer>
                
                <DetailItemContainer>
                  <IconContainer>
                    <WorkHistory sx={{
                      color: 'var(--primary-blue)',
                      fontSize: "1.5rem",
                      padding: "4px",
                      bgcolor: "#e3f2fd",
                      borderRadius: '8px',
                    }} />
                  </IconContainer>
                  <Box>
                    <DetailLabelText variant="body2">Minimum Experience</DetailLabelText>
                    <DetailValueText>{job.requirement?.text || "Not specified"}</DetailValueText>
                  </Box>
                </DetailItemContainer>
              </DetailsGrid>
            </AccordionDetails>
          </StyledAccordion>
          
          <StyledAccordion 
            expanded={expandedPanels.jobTitle}
            onChange={handleAccordionChange('jobTitle')}
          >
            <StyledAccordionSummary expandIcon={<ExpandMore />}>
              <AccordionTitleContainer>
                <AccordionTitle variant="h6">Job Title</AccordionTitle>
              </AccordionTitleContainer>
            </StyledAccordionSummary>
            <AccordionDetails>
              <DetailContentText>{job.title || "Not specified"}</DetailContentText>
            </AccordionDetails>
          </StyledAccordion>
          
          <StyledAccordion 
            expanded={expandedPanels.jobDescription}
            onChange={handleAccordionChange('jobDescription')}
          >
            <StyledAccordionSummary expandIcon={<ExpandMore />}>
              <AccordionTitleContainer>
                <AccordionTitle variant="h6">Job Description</AccordionTitle>
              </AccordionTitleContainer>
            </StyledAccordionSummary>
            <AccordionDetails>
              <DetailContentText sx={{ whiteSpace: 'pre-wrap' }}>{job.description || "Not specified"}</DetailContentText>
            </AccordionDetails>
          </StyledAccordion>
          
          <Box sx={{ 
            display: 'flex', 
            flexDirection: 'column', 
            gap: 2, 
            mt: 4, 
            mb: 4,
            width: '100%'
          }}>
            <Button
              variant="contained"
              endIcon={<ArrowForward />}
              onClick={onConfirm}
              sx={{
                bgcolor: "var(--primary-blue)",
                borderRadius: 10,
                py: 1.5,
                textTransform: "uppercase",
                fontWeight: "bold",
                boxShadow: 2,
                '&:hover': {
                  bgcolor: "#002266",
                },
              }}
            >
              Submit
            </Button>
            
            <Button
              variant="text"
              onClick={onClose}
              sx={{
                color: "var(--primary-blue)",
                textTransform: "none",
              }}
            >
              go back and edit
            </Button>
          </Box>
        </ContentContainer>
      </MainContainer>
    </>
  )
}

export default OverviewStep;
